import { useEffect, useState } from 'react';
import { Modal, Button, Textarea } from '@/components/atoms';

const REASON_MAX = 500;

export interface CancelOrderModalProps {
  open: boolean;
  orderTitle?: string;
  onClose: () => void;
  onSubmit: (reason: string) => void | Promise<void>;
}

/**
 * F-160 (C3): replaces the legacy `window.prompt()` flow on order decline.
 * Textarea for the (optional) reason + destructive confirm + ghost cancel.
 * Reason is trimmed; empty string is allowed (the backend accepts no-reason
 * cancellations and the previous prompt-based flow already passed `""`).
 */
export function CancelOrderModal({
  open,
  orderTitle,
  onClose,
  onSubmit,
}: CancelOrderModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(reason.trim());
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const title = orderTitle
    ? `Відхилити «${orderTitle}»?`
    : 'Відхилити замовлення?';

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Назад
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            Відхилити
          </Button>
        </>
      }
    >
      <Textarea
        name="cancel-reason"
        label="Причина (необовʼязково)"
        placeholder="Скажи коротко чому…"
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
        maxLength={REASON_MAX}
        rows={4}
      />
    </Modal>
  );
}
